import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { listEvents, getEvent } from '../services/eventsService';
import { getUserProfile } from '../services/profilesService';
import { getLayoutPublic } from '../services/layoutsService';
import { listVendorsLight } from '../services/vendorsService';
import { loadReservationsForLayoutDetailed, loadMyReservedBooths } from '../services/reservationsService';
import { createManualReservationsBulk, releaseBoothsBulk } from '../services/manualReservations.js';
import { sfVendorRegistration } from '../services/defaultsService';

import './ProfilePage.css';

const ManualReservation = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [publishedEvents, setPublishedEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventId, setEventId] = useState('');
  const [layoutId, setLayoutId] = useState('');
  const [layoutCells, setLayoutCells] = useState([]);
  const [levels, setLevels] = useState([]);
  const [layoutCustomLevels, setLayoutCustomLevels] = useState({});
  const [selectedLevel, setSelectedLevel] = useState('');
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('');
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [selectedBoothId, setSelectedBoothId] = useState('');
  const [selectedBooths, setSelectedBooths] = useState([]); // array of { id, number, level, corner, price }
  const [actionType, setActionType] = useState('reserve'); // 'reserve' | 'release'
  const [reservedBooths, setReservedBooths] = useState([]);
  const [rawReservedBooths, setRawReservedBooths] = useState([]);
  const [loadingReservedBooths, setLoadingReservedBooths] = useState(false);
  const [includeCreditPaid, setIncludeCreditPaid] = useState(false);
  const [paymentType, setPaymentType] = useState('Check');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [lastResults, setLastResults] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setEventsLoading(true);
        const all = await listEvents();
        const havingLayout = (all || []).filter(ev => ev?.layoutId && (ev?.status || '').toLowerCase() !== 'archived');

        // Apply role-based filtering: Admin sees all, EventAdmin checks `eventAdmins`, Organizer checks `organizers`, others see none
        let filteredHaving = havingLayout;
        try {
          if (currentUser && currentUser.uid) {
            try {
              const prof = await getUserProfile(currentUser.uid);
              const uidLower = String(currentUser.uid || '').toLowerCase();
              if (prof?.userType === 'EventAdmin') {
                filteredHaving = (havingLayout || []).filter(ev => Array.isArray(ev.eventAdmins) && ev.eventAdmins.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType === 'Organizer') {
                filteredHaving = (havingLayout || []).filter(ev => Array.isArray(ev.organizers) && ev.organizers.some(a => String(a || '').toLowerCase() === uidLower));
              } else if (prof?.userType !== 'Admin') {
                filteredHaving = [];
              }
            } catch (profileErr) {
              filteredHaving = [];
            }
          }
        } catch (e) {
          filteredHaving = [];
        }

        const entries = await Promise.all(filteredHaving.map(async (ev) => {
          try {
            const ly = await getLayoutPublic(ev.layoutId);
            const status = (ly?.status || '').toLowerCase();
            if (status !== 'published') return null;
            return { eventId: ev.id, eventName: ev.name || ev.id, layoutId: ly.id};
          } catch { return null; }
        }));
        const filtered = entries.filter(Boolean).sort((a, b) => String(a.eventName).localeCompare(String(b.eventName)));
        if (!cancelled) {
          setPublishedEvents(filtered);
        }
      } catch (e) {
        console.warn('ManualReservation: failed to load events', e);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (layoutId) {
          const ly = await getLayoutPublic(layoutId);
          if (cancelled) return;
          const cells = Array.isArray(ly?.cellData) ? ly.cellData : [];
          setLayoutCells(cells);
          setLayoutCustomLevels(ly?.customLevels || {});
          const levelSet = new Set(cells.map(c => c.level).filter(l => l && l !== 'Invisible'));
          const levelList = Array.from(levelSet).sort();
          setLevels(levelList);
          if (!selectedLevel && levelList.length > 0) setSelectedLevel('All');
        } else {
          setLayoutCells([]);
          setLevels([]);
          setSelectedLevel('');
        }
      } catch (e) {
        console.warn('ManualReservation: failed to load layout', e);
      }
    })();
    return () => { cancelled = true; };
  }, [layoutId]);

  // Load vendors (lightweight list) on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listVendorsLight();
        if (!cancelled) setVendors(list || []);
      } catch (e) {
        console.warn('ManualReservation: failed to load vendors', e);
        if (!cancelled) setVendors([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (eventId && layoutId) {
          const map = await loadReservationsForLayoutDetailed({ eventId, layoutId });
          if (!cancelled) setAvailabilityMap(map || {});
        } else {
          setAvailabilityMap({});
        }
      } catch (e) {
        console.warn('ManualReservation: failed to load reservations', e);
        if (!cancelled) setAvailabilityMap({});
      }
      // If current action is release and vendor selected, also load reserved booths for that vendor
      try {
        if (actionType === 'release' && selectedVendorId) {
          setLoadingReservedBooths(true);
          const reserved = await loadMyReservedBooths({ vendorId: selectedVendorId });
          if (!cancelled) {
            // Keep raw results for diagnostics/UI feedback
            let raw = (reserved || []).filter(r => String(r.eventId) === String(eventId) && String(r.layoutId) === String(layoutId));
            setRawReservedBooths(raw);
            let filtered = raw.slice();
            if (!includeCreditPaid) {
              filtered = filtered.filter(r => {
                const pm = (r.paymentMethod || r.paymentType || (r.metadata && r.metadata.paymentType) || '').toString().toLowerCase();
                return pm === 'check' || pm === 'cash' || pm === 'manual-cc';
              });
            }
            setReservedBooths(filtered);
          }
        } else {
          if (!cancelled) { setReservedBooths([]); setRawReservedBooths([]); }
        }
      } catch (e) {
        if (!cancelled) setReservedBooths([]);
      } finally {
        if (!cancelled) setLoadingReservedBooths(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, layoutId, actionType, selectedVendorId, includeCreditPaid]);

  // Clear selection when vendor or action changes
  useEffect(() => {
    setSelectedBoothId('');
    setSelectedBooths([]);
    setReason('');
  }, [actionType, selectedVendorId]);

  // When the selected vendor changes, reset other fields (level, selections, reserved list, action)
  useEffect(() => {
    setSelectedBoothId('');
    setSelectedBooths([]);
    setSelectedLevel('');
    setActionType('reserve');
    setReservedBooths([]);
    setVendorFilter('');
  }, [selectedVendorId]);

  const layoutBoothsByLevel = useMemo(() => {
    const map = {};
    for (const c of layoutCells) {
      const lvl = c.level || '';
      if (!lvl || c.status === 'Invisible') continue;
      if (!map[lvl]) map[lvl] = [];
      map[lvl].push(c);
    }
    return map;
  }, [layoutCells]);

  const availableBoothsForLevel = (lvl) => {
    if (!lvl) return [];
    const lvlStr = String(lvl || '').trim();
    const target = lvlStr.toLowerCase();
    let cells = [];
    if (target === 'all') {
      // combine all levels
      cells = Object.values(layoutBoothsByLevel).reduce((acc, arr) => acc.concat(arr || []), []);
    } else {
      cells = layoutBoothsByLevel[lvlStr] || [];
    }
    return cells
      .filter(c => {
        if (!c) return false;
        if (c.status === 'Invisible' || String(c.level || '').toLowerCase() === 'invisible') return false;
        const cellLevel = String(c.level || '').trim().toLowerCase();
        if (target !== 'all' && cellLevel !== target) return false;
        const entry = availabilityMap && availabilityMap[c.id];
        // If there's no reservation entry, it's available
        if (!entry) return true;
        const toMillis = (ts) => {
          try {
            if (!ts) return null;
            if (typeof ts.toMillis === 'function') return ts.toMillis();
            if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
            if (typeof ts === 'number') return ts;
            const parsed = new Date(ts).getTime();
            return Number.isFinite(parsed) ? parsed : null;
          } catch {
            return null;
          }
        };
        // Otherwise, include it only if it's an On-Hold entry.
        // Handle string or object entries.
        try {
          if (typeof entry === 'string') {
            const s = entry.toLowerCase();
            return s.includes('hold');
          }
          if (typeof entry === 'object') {
            const stat = (entry.status || '').toString().toLowerCase();
            if (stat === 'pending') {
              const expiresMs = toMillis(entry.expiresAt) ?? toMillis(entry.expiresAtMs);
              // Expired Pending booths should be treated as available.
              if (typeof expiresMs === 'number' && expiresMs <= Date.now()) return true;
              return false;
            }
            if (stat && stat.includes('hold')) return true;
            if (entry.holdName || entry.hold_name) return true;
            return false;
          }
        } catch (e) {
          return false;
        }
        return false;
      })
      .sort((a, b) => {
        const ta = String(a.number ?? a.boothNumber ?? a.id);
        const tb = String(b.number ?? b.boothNumber ?? b.id);
        return ta.localeCompare(tb, undefined, { numeric: true });
      });
  };

  const reservedBoothsForSelection = () => {
    return (reservedBooths || []).slice().sort((a, b) => String((a.boothNumber || a.boothId)).localeCompare(String((b.boothNumber || b.boothId)), undefined, { numeric: true }));
  };

  const vendorOptions = useMemo(() => {
    const q = (vendorFilter || '').trim().toLowerCase();
    let list = [];
    if (!q) list = vendors.slice();
    else list = vendors.filter(v => (v.name || '').toLowerCase().includes(q) || (v.vendorNumber || '').toLowerCase().includes(q));
    // Ensure the currently selected vendor is visible: if it's not in the filtered list,
    // try to find it in the full vendors list and place it at the front.
    if (selectedVendorId) {
      const foundInList = list.some(v => v.id === selectedVendorId);
      if (!foundInList) {
        const sel = vendors.find(v => v.id === selectedVendorId);
        if (sel) list.unshift(sel);
      } else {
        // move selected vendor to front for visibility
        list = list.filter(v => v.id !== selectedVendorId);
        const sel = vendors.find(v => v.id === selectedVendorId);
        if (sel) list.unshift(sel);
      }
    }
    return list;
  }, [vendors, vendorFilter]);

  const parseBulkResult = (result) => {
    if (!result) {
      return { ok: false, message: 'Unknown reservation response.' };
    }

    if (result.status === 'rejected') {
      const reason = result.reason;
      return {
        ok: false,
        message: (reason && reason.message) ? reason.message : String(reason || 'Request failed.'),
      };
    }

    // Manual callable responses may be nested as { data: ... } or { result: ... }.
    const value = result.value;
    const payload = (value && typeof value === 'object')
      ? (value.data ?? value.result ?? value)
      : value;

    if (payload && typeof payload === 'object' && payload.success === false) {
      return {
        ok: false,
        message: payload.message || payload.error || 'Request was rejected by backend.',
      };
    }

    return { ok: true, message: '' };
  };

  const handleMakeReservation = async () => {
    setError('');
    setMessage('');
    if (!eventId || !layoutId || !selectedVendorId) {
      setError('Event, Vendor and Vendor selection are required.');
      return;
    }
    if (selectedBooths.length === 0) {
      setError('Add at least one booth to the list before performing the action.');
      return;
    }
    if (!reason || !reason.trim()) {
      setError('Reason is required for this action.');
      return;
    }
    setSaving(true);
    try {
      if (!currentUser) { setError('Login required'); return; }
      const createdByProfileId = currentUser.uid;
      // snapshot selected booths so we can display results after clearing selection
      const snapshotBooths = (selectedBooths || []).slice();
      let results;
      if (actionType === 'reserve') {
        results = await createManualReservationsBulk(snapshotBooths, { createdByProfileId, eventId, layoutId, vendorId: selectedVendorId, metadata: { 'action': 'Reserve Booth(s)', paymentType, reason: (reason || '').trim() } });
      } else {
        results = await releaseBoothsBulk(snapshotBooths, { createdByProfileId, eventId, layoutId, vendorId: selectedVendorId, metadata: { 'action': 'Release Booth(s)', reason: (reason || '').trim() } });
      }
      setLastResults({ items: snapshotBooths, results });
      const failures = (results || []).map(parseBulkResult).filter(r => !r.ok);
      if (failures.length > 0) {
        const first = failures[0];
        console.error('ManualReservation error', first);
        setError(first.message || 'One or more reservation requests failed.');
      } else {
        setMessage(actionType === 'reserve' ? 'Reservation(s) created successfully.' : 'Release(s) executed successfully.');
        setSelectedBooths([]);
        // Fire-and-forget: notify external SF vendor registration for newly reserved or released booths
        try {
          if ((actionType === 'reserve' || actionType === 'release') && selectedVendorId && eventId) {
            const appSettings = { enableLogs: false, enableEncryption: true };
            const res = sfVendorRegistration(appSettings, eventId, selectedVendorId);
            if (appSettings.enableLogs) 
              console.log("[ManualReservation] sfVendorRegistration result: ", res);
          }
        } catch (err) {
          console.warn('[ManualReservation] Error initiating sfVendorRegistration [non-blocking]: ', err && err.message ? err.message : err);
        }
      }
      try { const map = await loadReservationsForLayoutDetailed({ eventId, layoutId }); setAvailabilityMap(map || {}); } catch {}
      if (actionType === 'release') {
        try {
          const reserved = await loadMyReservedBooths({ vendorId: selectedVendorId });
          let filtered = (reserved || []).filter(r => String(r.eventId) === String(eventId) && String(r.layoutId) === String(layoutId));
          if (!includeCreditPaid) {
            filtered = filtered.filter(r => {
              const pm = (r.paymentMethod || r.paymentType || (r.metadata && r.metadata.paymentType) || '').toString().toLowerCase();
              return pm === 'check' || pm === 'cash';
            });
          }
          setReservedBooths(filtered);
        } catch {}
      }
    } catch (e) {
      console.error('ManualReservation error', e);
      setError((e && e.message) ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amountInCents) => {
    const cents = typeof amountInCents === 'number' && !isNaN(amountInCents) ? amountInCents : 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  };

  const addSelectedBooth = () => {
    setError('');
    setMessage('');
    if (!selectedBoothId) { setError('Select a booth to add.'); return; }
    if (actionType === 'reserve') {
      const cell = layoutCells.find(c => c.id === selectedBoothId);
      if (!cell) { setError('Selected booth not found.'); return; }
      if (selectedBooths.some(b => b.id === cell.id)) { setError('Booth already added.'); return; }
      // Derive price from layout customLevels (preferred), falling back to any price on the cell
      const isCorner = cell.corner === 'Y' || cell.corner === true;
      const levelData = (layoutCustomLevels && cell.level) ? layoutCustomLevels[cell.level] : null;
      let priceVal = null;
      if (levelData) {
        priceVal = isCorner ? levelData.cornerPrice : levelData.inlinePrice;
        if (typeof priceVal === 'string' && priceVal !== '') {
          const parsed = parseInt(priceVal, 10);
          priceVal = Number.isNaN(parsed) ? null : parsed;
        }
      }
      if (priceVal === null || typeof priceVal !== 'number') {
        priceVal = (typeof cell.price === 'number') ? cell.price : (typeof cell.priceInCents === 'number' ? cell.priceInCents : 0);
      }
      const statusRaw = availabilityMap && availabilityMap[cell.id];
      const status = statusRaw ? (typeof statusRaw === 'string' ? statusRaw : (statusRaw.status || JSON.stringify(statusRaw))) : (cell.status || 'Available');
      const newItem = {
        id: cell.id,
        row: cell.row || '',
        col: cell.col || '',
        number: cell.number ?? cell.boothNumber ?? cell.id,
        level: cell.level ?? '',
        corner: isCorner ? 'Y' : 'N',
        price: priceVal,
        status,
      };
      setSelectedBooths(prev => {
        const next = [...prev, newItem];
        // sort by level then booth number
        next.sort((x, y) => {
          const aLevel = x.level || '';
          const bLevel = y.level || '';
          const levelCmp = String(aLevel).localeCompare(String(bLevel));
          if (levelCmp !== 0) return levelCmp;
          const an = x.number; const bn = y.number;
          const anNum = typeof an === 'number' ? an : (parseFloat(String(an)) || null);
          const bnNum = typeof bn === 'number' ? bn : (parseFloat(String(bn)) || null);
          if (anNum != null && bnNum != null) return anNum - bnNum;
          return String(an).localeCompare(String(bn), undefined, { numeric: true });
        });
        return next;
      });
      return;
    }

    // release flow: selectedBoothId refers to a reservedBooths entry
    const res = reservedBooths.find(r => r.boothId === selectedBoothId);
    if (!res) { setError('Selected reserved booth not found.'); return; }
    if (selectedBooths.some(b => b.id === res.boothId)) { setError('Booth already added.'); return; }
    const details = res.boothDetails || {};
    const newItem = {
      id: res.boothId,
      number: res.boothNumber || res.boothId,
      boothNumber: res.boothNumber || res.boothId,
      level: details.level || '',
      row: details.row || '',
      col: details.col || '',
      corner: (details.corner === 'Y' || details.corner === 'y' || details.corner === true) ? 'Y' : 'N',
      price: details.price || 0,
      paymentType: (res.paymentMethod || res.paymentType || (res.metadata && res.metadata.paymentType) || '')
    };
    // attach status for release items
    try {
      const statusRaw = availabilityMap && availabilityMap[res.boothId];
      newItem.status = statusRaw ? (typeof statusRaw === 'string' ? statusRaw : (statusRaw.status || JSON.stringify(statusRaw))) : 'Reserved';
    } catch (e) { newItem.status = 'Reserved'; }
    setSelectedBooths(prev => {
      const next = [...prev, newItem];
      next.sort((x, y) => {
        const aLevel = x.level || '';
        const bLevel = y.level || '';
        const levelCmp = String(aLevel).localeCompare(String(bLevel));
        if (levelCmp !== 0) return levelCmp;
        const an = x.number; const bn = y.number;
        const anNum = typeof an === 'number' ? an : (parseFloat(String(an)) || null);
        const bnNum = typeof bn === 'number' ? bn : (parseFloat(String(bn)) || null);
        if (anNum != null && bnNum != null) return anNum - bnNum;
        return String(an).localeCompare(String(bn), undefined, { numeric: true });
      });
      return next;
    });
  };

  const removeSelectedBooth = (id) => {
    setSelectedBooths(prev => prev.filter(b => b.id !== id));
  };

  const closePopup = () => {
    // determine if last operation was a complete success (no rejections and no error)
    const wasSuccess = lastResults && Array.isArray(lastResults.results) && lastResults.results.every(r => r && r.status === 'fulfilled') && !error;
    setLastResults(null);
    setMessage('');
    setError('');
    if (wasSuccess) {
      // reset form to defaults; if we have published events, select the first
      const firstEvent = (publishedEvents && publishedEvents.length > 0) ? publishedEvents[0] : null;
      setEventId(firstEvent ? firstEvent.eventId : '');
      setLayoutId(firstEvent ? firstEvent.layoutId : '');
      setLayoutCells([]);
      setLevels([]);
      setLayoutCustomLevels({});
      setSelectedLevel('');
      setAvailabilityMap({});
      setVendorFilter('');
      setSelectedVendorId('');
      setSelectedBoothId('');
      setSelectedBooths([]);
      setActionType('reserve');
      setReservedBooths([]);
      setLoadingReservedBooths(false);
      setPaymentType('Check');
      setReason('');
    }
  };

  const totalCost = selectedBooths.reduce((sum, b) => sum + (typeof b.price === 'number' ? b.price : 0), 0);

  return (
    <div className="profile-container vendor-admin-container" style={{ maxWidth: 720 }}>
      <div className="profile-header-row">
        <button type="button" className="back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>Close</button>
      </div>
      <h2 className="profile-title">Admin: Manual Reservation</h2>
      <div className="profile-hint">Create a reservation for any vendor, bypassing cart/payment.</div>
      {error && <div className="profile-error">{error}</div>}
      {message && <div className="profile-hint" style={{ color: '#2e7d32' }}>{message}</div>}

      <div className="profile-grid" style={{ gridTemplateColumns: '1fr', gap: 12 }}>
        <div>
          <label className="profile-label">Select Event</label>
          <select className="profile-input" value={eventId} onChange={(e) => {
            const val = e.target.value;
            setEventId(val);
            const opt = publishedEvents.find(p => p.eventId === val);
            if (opt) { setLayoutId(opt.layoutId); }
          }} disabled={eventsLoading || publishedEvents.length === 0}>
            {eventsLoading && <option value="">Loading events…</option>}
            {!eventsLoading && publishedEvents.length === 0 && <option value="">No published events</option>}
            {!eventsLoading && publishedEvents.length > 0 && (
              <>
                <option value="" disabled>Select an event…</option>
                {publishedEvents.map(p => <option key={p.eventId} value={p.eventId}>{p.eventName}</option>)}
              </>
            )}
          </select>
        </div>

        <div>
          <label className="profile-label">Select Vendor</label>
          <input className="profile-input" placeholder="Type to filter vendors" value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} />
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Click a vendor to select</div>
            {/* Sticky selected vendor header */}
            {selectedVendorId ? (
              // Show only sticky selected vendor header when a selection is made
              (() => {
                const sel = vendors.find(v => v.id === selectedVendorId) || null;
                const label = sel ? ((sel.name || sel.vendorNumber || sel.id) + (sel.city ? ` — ${sel.city}` : '')) : 'Selected vendor';
                return (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: '#0b4470', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>Selected vendor</div>
                    </div>
                    <div>
                      <button type="button" className="cancel-btn" onClick={() => setSelectedVendorId('')}>Clear</button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="vendor-list" style={{ border: '1px solid #ddd', borderRadius: 4, maxHeight: 220, overflow: 'auto' }}>
                {vendorOptions.length === 0 && (
                  <div style={{ padding: 8, color: '#666' }}>No vendors match</div>
                )}
                {vendorOptions.map(v => (
                  <div
                    key={v.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedVendorId(v.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedVendorId(v.id); }}
                    style={{
                      padding: 8,
                      borderBottom: '1px solid #f3f3f3',
                      cursor: 'pointer',
                      background: selectedVendorId === v.id ? '#eef7ff' : 'transparent',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {(v.name || v.vendorNumber || v.id) + (v.city ? ` — ${v.city}` : '')}
                  </div>
                ))}
              </div>
            )}
            {actionType === 'release' && selectedVendorId && (
              <div style={{ marginTop: 6, fontSize: 13, color: '#333' }}>
                {loadingReservedBooths ? 'Loading reserved booths…' : `Reserved booths for this vendor on selected event/layout: ${rawReservedBooths.length}`}
                {!loadingReservedBooths && rawReservedBooths.length > 0 && reservedBooths.length === 0 && (
                  (() => {
                    const counts = rawReservedBooths.reduce((acc, r) => {
                      const pm = (r.paymentMethod || r.paymentType || (r.metadata && r.metadata.paymentType) || '') || '';
                      const key = (pm || 'CreditCard').toString();
                      acc[key] = (acc[key] || 0) + 1;
                      return acc;
                    }, {});
                    const parts = Object.entries(counts).map(([k, v]) => `${v} ${k}`);
                    return (<div style={{ fontSize: 12, color: '#a00', marginTop: 6 }}>No booths match the current payment filter. Found: {parts.join(', ')}.</div>);
                  })()
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          {selectedVendorId && (
            <div>
              <label className="profile-label">Select Action</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="radio" name="manualAction" value="reserve" checked={actionType === 'reserve'} onChange={() => setActionType('reserve')} />
                  <span style={{ marginLeft: 6 }}>Reserve Booth</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="radio" name="manualAction" value="release" checked={actionType === 'release'} onChange={() => setActionType('release')} />
                  <span style={{ marginLeft: 6 }}>Release Booth</span>
                </label>
              </div>
            </div>
          )}
          {selectedVendorId && actionType === 'reserve' && (
            <>
              <label className="profile-label">Select Booth Level</label>
              <select className="profile-input" value={selectedLevel} onChange={e => setSelectedLevel(e.target.value)} disabled={levels.length === 0}>
                {levels.length === 0 && <option value="">No levels</option>}
                <option value="All">All</option>
                {levels.map(l => <option key={l} value={l}>{(layoutCustomLevels && layoutCustomLevels[l] && layoutCustomLevels[l].name) ? layoutCustomLevels[l].name : l}</option>)}
              </select>
            </>
          )}
        </div>

        {selectedVendorId && (
          <div>
            <label className="profile-label">Select Booth #</label>
            {actionType === 'release' && selectedVendorId && (
              <div style={{ margin: '8px 0 12px 0' }}>
                <label style={{ fontSize: 13 }}>
                  <input type="checkbox" style={{ marginRight: 8 }} checked={includeCreditPaid} onChange={e => setIncludeCreditPaid(!!e.target.checked)} />
                  Include Booths Paid by Credit-Card? (default: unchecked)
                </label>
                <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                  It's better to release credit-card paid booths via a refund transaction. If released here, they will not be refunded. When unchecked, only booths with payment method 'check' or 'cash' are shown.
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="profile-input" value={selectedBoothId} onChange={e => setSelectedBoothId(e.target.value)} disabled={actionType === 'reserve' ? !selectedLevel : (loadingReservedBooths || reservedBooths.length === 0)}>
                <option value="">Select a booth…</option>
                {actionType === 'reserve' && availableBoothsForLevel(selectedLevel).map(b => {
                  const isCorner = (b.corner === 'Y' || b.corner === 'y' || b.corner === true);
                  const statusRaw = availabilityMap && availabilityMap[b.id];
                  const status = statusRaw ? (typeof statusRaw === 'string' ? statusRaw : (statusRaw.status || JSON.stringify(statusRaw))) : (b.status || 'Available');
                  return (
                    <option key={b.id} value={b.id}>{(b.number ?? b.boothNumber ?? b.id)} — {b.id} — {isCorner ? 'Corner' : 'Inline'} — {status}</option>
                  );
                })}
                {actionType === 'release' && reservedBoothsForSelection().map(r => {
                  const det = r.boothDetails || {};
                  const isCorner = (det.corner === 'Y' || det.corner === 'y' || det.corner === true);
                  const pmRaw = (r.paymentMethod || (r.metadata && r.metadata.paymentType) || null);
                  const pmDisplay = pmRaw ? pmRaw : 'Credit-Card';
                  const statusRaw = availabilityMap && availabilityMap[r.boothId];
                  const status = statusRaw ? (typeof statusRaw === 'string' ? statusRaw : (statusRaw.status || JSON.stringify(statusRaw))) : 'Reserved';
                  return (
                    <option key={r.boothId} value={r.boothId}>{(r.boothNumber ?? r.boothId)} — {r.boothId} — {isCorner ? 'Corner' : 'Inline'} — {pmDisplay} — {status}</option>
                  );
                })}
              </select>
              <button type="button" className="apply-btn" onClick={addSelectedBooth} disabled={!selectedBoothId}>Select</button>
            </div>
            <div className="profile-hint">{actionType === 'reserve' ? 'Only currently available booths are shown.' : (loadingReservedBooths ? 'Loading reserved booths…' : (reservedBooths.length === 0 ? 'No reserved booths for this vendor on the selected event/layout.' : 'Showing reserved booths for selected vendor.'))}</div>

            {selectedBooths.length > 0 && (
              <div style={{ marginTop: 12, gridColumn: '1 / -1' }}>
                <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Level</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Booth #</th>
                        <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                      {actionType === 'release' && <th style={{ textAlign: 'left', padding: '8px' }}>Payment Method</th>}
                      <th style={{ textAlign: 'left', padding: '8px' }}>Type</th>
                      <th style={{ textAlign: 'right', padding: '8px' }}>Price</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBooths.map(b => (
                      <tr key={b.id}>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{b.level}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee', fontWeight: 600 }}>{b.number}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{b.status || '-'}</td>
                        {actionType === 'release' && <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{b.paymentMethod || 'Credit-Card'}</td>}
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{(b.corner === 'Y' || b.corner === true) ? 'Corner' : 'Inline'}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(b.price)}</td>
                        <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                          <button type="button" className="cancel-btn" onClick={() => removeSelectedBooth(b.id)}>Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, fontWeight: 700 }}>
                  Total: {formatCurrency(totalCost)}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedVendorId && selectedBooths.length > 0 && (
          <div>
            {actionType === 'reserve' ? (
              <>
                <label className="profile-label">Payment Type</label>
                <select className="profile-input" value={paymentType} onChange={e => setPaymentType(e.target.value)}>
                  <option>Check</option>
                  <option>Cash</option>
                  <option>Manual-CC</option>
                  <option>Exchange</option>
                </select>
                <label className="profile-label">Reason <span style={{ color: '#b00020' }}>*</span></label>
                <textarea className="profile-input" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for this action (required)" />
              </>
            ) : (
              <>
                <label className="profile-label">Reason <span style={{ color: '#b00020' }}>*</span></label>
                <textarea className="profile-input" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for release (required)" />
              </>
            )}
          </div>
        )}
      </div>

      {selectedVendorId && selectedBooths.length > 0 && (
        <div className="profile-actions">
          <button className="apply-btn" onClick={handleMakeReservation} disabled={saving || !eventId || !layoutId || !selectedVendorId || selectedBooths.length === 0}>{saving ? 'Saving…' : (actionType === 'reserve' ? 'Make Reservation' : 'Release Booths')}</button>
          <button className="cancel-btn" onClick={() => { setSelectedVendorId(''); setSelectedBoothId(''); setMessage(''); setError(''); }}>Reset</button>
        </div>
      )}

      <div className="profile-meta">Note: This operation creates a reservation and logs it directly.</div>
      {(lastResults || message || error) && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="popup-header">
              <h3 style={{ margin: 0 }}>{error ? 'Operation completed with errors' : (message || 'Operation result')}</h3>
              <button type="button" className="cancel-btn" onClick={closePopup}>Close</button>
            </div>
            <div className="popup-body" style={{ maxHeight: '60vh', overflow: 'auto' }}>
              {message && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{message}</div>}
              {error && <div style={{ color: '#b00020', marginBottom: 8 }}>{error}</div>}
              {lastResults && lastResults.items && (
                <table className="profile-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Booth</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Level</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastResults.items.map((it, idx) => {
                      const res = (lastResults.results && lastResults.results[idx]) || null;
                      const status = res ? (res.status === 'fulfilled' ? 'OK' : 'Failed') : 'Unknown';
                      let details = '';
                      if (res && res.status === 'rejected') {
                        const r = res.reason;
                        details = (r && r.message) ? r.message : String(r || 'Error');
                      }
                      return (
                        <tr key={it.id}>
                          <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{it.number}</td>
                          <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{it.level}</td>
                          <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{it.corner ? 'Corner' : 'Inline'}</td>
                          <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{status}</td>
                          <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>{details}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManualReservation;
